import { Button, Input, Select, Textarea } from "@mantine/core";
import RoutesInstance from "@routes/instances";
import { useMutation, useQuery } from "@tanstack/react-query";
import Common from "@utilities/common";
import { Form, Formik, FormikHelpers } from "formik";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IFormData } from "./types";
import { Schema } from "./validations";
import Editor from "@components/editor";
import { VscLoading } from "react-icons/vsc";

const FormContainer: FC = () => {
    const [initialValues] = useState<IFormData>({
        enName: "",
        faName: "",
        url: "",
        parentId: "",
        description: "",
        seoDescription: "",
        seoKeywords: "",
    });
    const [categoriesQuery, setCategoriesQuery] = useState("");

    const Navigate = useNavigate();

    const { data: categoriesData, isLoading: isCategoriesDataLoading } =
        useQuery(
            [
                "getCategories",
                {
                    query: categoriesQuery,
                },
            ],
            () =>
                Common.api.categories
                    .GetAllWithoutFilter()
                    .then((res) => res.data),
            {
                retry: false,
            },
        );

    const { mutate, isLoading } = useMutation(
        ({
            faName,
            enName,
            url,
            parentId,
            description,
            seoDescription,
            seoKeywords,
        }: IFormData) =>
            Common.api.categories.Create({
                faName,
                enName,
                url,
                parentId: parentId ? +parentId : null,
                ...(description ? { description } : {}),
                ...(seoDescription ? { seoDescription } : {}),
                ...(seoKeywords ? { seoKeywords } : {}),
            }),
        {
            onSuccess: () => {
                Navigate(RoutesInstance.categories);
            },
        },
    );

    const handleSubmit = async (
        values: IFormData,
        formik: FormikHelpers<IFormData>,
    ) => {
        mutate(values);
    };

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={Schema}
            validateOnBlur={true}
            validateOnChange={true}>
            {({ values, errors, handleChange, handleBlur, isValid }) => (
                <Form>
                    <div className="mb-4">
                        <Input.Wrapper
                            id="faName"
                            label="نام فارسی"
                            required
                            error={errors.faName}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="faName"
                                name="faName"
                                type="text"
                                placeholder="نام فارسی"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.faName}
                                invalid={!!errors.faName}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <Input.Wrapper
                            id="enName"
                            label="نام انگلیسی"
                            required
                            error={errors.enName}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="enName"
                                name="enName"
                                type="text"
                                placeholder="نام انگلیسی"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.enName}
                                invalid={!!errors.enName}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <Input.Wrapper
                            id="url"
                            label="آدرس (انگلیسی - بدون فاصله همراه با'-')"
                            required
                            error={errors.url}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="url"
                                name="url"
                                type="text"
                                placeholder="آدرس (انگلیسی - بدون فاصله همراه با'-')"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.url}
                                invalid={!!errors.url}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="mb-4">
                        <Select
                            data={
                                (categoriesData?.result || [])
                                    .filter((item) => !item.parent)
                                    .map((item) => ({
                                        label: item.faName,
                                        value: item.id.toString(),
                                    })) || []
                            }
                            id="parentId"
                            name="parentId"
                            value={values.parentId || ""}
                            placeholder="انتخاب"
                            onChange={(value) => {
                                handleChange({
                                    target: {
                                        id: "parentId",
                                        name: "parentId",
                                        value: value,
                                    },
                                });
                            }}
                            label="دسته بندی والد"
                            variant="filled"
                            color="blue"
                            searchable
                            onSearchChange={(value) =>
                                setCategoriesQuery(value)
                            }
                            searchValue={categoriesQuery}
                            icon={
                                isCategoriesDataLoading &&
                                categoriesQuery.length > 2 ? (
                                    <VscLoading className="animate-spin text-2xl" />
                                ) : (
                                    <></>
                                )
                            }
                        />
                    </div>

                    <div className="mb-4">
                        <Editor
                            value={values.description}
                            handleChange={(value: string) =>
                                handleChange({
                                    target: {
                                        id: "description",
                                        name: "description",
                                        value,
                                    },
                                })
                            }
                        />
                    </div>

                    <div className="mb-4">
                        <Textarea
                            variant="filled"
                            color="blue"
                            id="seoDescription"
                            name="seoDescription"
                            placeholder="توضیحات سئو"
                            label="توضیحات سئو"
                            minRows={10}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={isLoading}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}
                            error={errors.seoDescription}
                            value={values.seoDescription}
                        />
                    </div>

                    <div className="mb-10">
                        <Input.Wrapper
                            id="seoKeywords"
                            label="کلمات کلیدی سئو"
                            error={errors.seoKeywords}
                            classNames={{
                                error: "text-sm",
                                label: "mb-2 text-gray-700 font-medium",
                            }}>
                            <Input
                                variant="filled"
                                color="blue"
                                id="seoKeywords"
                                name="seoKeywords"
                                type="text"
                                placeholder="کلمات کلیدی سئو"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={isLoading}
                                value={values.seoKeywords}
                                invalid={!!errors.seoKeywords}
                            />
                        </Input.Wrapper>
                    </div>

                    <div className="text-center">
                        <Button
                            variant="filled"
                            className="transition-all duration-300 px-16"
                            color="blue"
                            radius={9999}
                            type="submit"
                            disabled={!isValid || isLoading}
                            loading={isLoading}>
                            ثبت
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default FormContainer;
